import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import styled from 'styled-components';
import Vendor, { ChangedValues, MergedAttributes } from 'data/Vendor';
import { isBrandLevelRoles, isVendor } from 'data/User';
import Attribute, { AttributeValue, AttributeValueRequest, empty } from 'data/Attribute';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import DroppableColumn from 'view/DroppableColumn';
import InfoIcon from 'components/UI/InfoIcon';
import PageHeading from 'components/UI/PageHeading';
import { renderToast, updateToast, toast } from 'components/UI/ToastNotification';
import { Color, Size, Percent, FontWeight } from 'lib/styles';
import { move, orderByArray } from 'lib/Array';
import { useAuth } from 'lib/Auth';
import { mergeAttributes } from 'lib/attributesScreenHelper';
import MergeAttributeModal from 'common/MergeAttributesModal';
import AddAttributeValueModal from 'common/AddAttributeValueModal';
import AddBulkAttributesModal from 'common/AddBulkAttributesModal';
import { Main, Background } from 'styles/themeColors';
import ImportXLSXAttributeValueModal from 'common/ImportXLSXAttributeValueModal';
import { ATTRIBUTE_CONSTANTS, ATTRIBUTE_MESSAGES, ATTRIBUTE_TOOLTIPS, ATTRIBUTE_TOAST_IDS, ATTRIBUTE_BUTTONS } from 'constants/attributes';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { BusinessUnit } from 'data/BusinessUnit';
import { getAttributesPerBusinessUnit, reorderRequest } from 'requests/atribute';
import LoadingIndicator from 'components/UI/LoadingIndicator';

const List = styled.ul`
  margin: 0px;
  padding: 0px;
  list-style-type: none;
  color: ${Color.black};
  width: ${Percent.full};
  height: ${ATTRIBUTE_CONSTANTS.LIST_HEIGHT};
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: flext-start;
`;

const EmptyMessage = styled.span`
  font-size: 24px;
  padding: 24px;
`;


const StyledInfoIcon = styled(InfoIcon)`
  display: flex;
  margin-left: ${Size.huge};
  margin-top: 0.1rem;
`;

const Checkbox = styled.input`
  width: ${ATTRIBUTE_CONSTANTS.CHECKBOX_SIZE};
  height: ${ATTRIBUTE_CONSTANTS.CHECKBOX_SIZE};

  &:hover{
    background-color: ${Main.success} !important;
    cursor: pointer;
  }
`;

const ListItem = styled.li<any>`
  margin: 0px;
  padding: 18px 14px;
  display: block;
  border: 1px solid ${Color.veryLightGray};
  cursor: pointer;
  font-size: ${Size.medium};
  font-weight: ${FontWeight.bold};
  background-color: ${Background.white};

  &:hover {
    border-color: ${Main.accent};
  }

  &:active {
    -webkit-box-shadow: inset 0px 0px 10px ${Color.darkGray};
  }

  ${({ active }) => active && `
    background: ${Color.pattensBlue};
    -webkit-box-shadow: inset 0px 0px 5px ${Color.darkGray};
    -moz-box-shadow: inset 0px 0px 5px ${Color.darkGray};
    box-shadow: inset 0px 0px 5px ${Color.darkGray};
    outline: none;
  `}
`;

const ValuesCount = styled.span`
  display: flex;
  color: ${Main.accent};
  font-size: ${Size.medium};
`;

const AttributesTitle = styled.div`
  padding-left: 15px;
  display: flex;
  font-size: 22px;
  font-weight: ${FontWeight.bold};
  justify-content: center;
  align-items: center;
`;

const SelectAttributeMessage = styled.div<any>`
  text-align: center;
  vertical-align: middle;
  font-weight: ${FontWeight.bold};
  font-size: ${ATTRIBUTE_CONSTANTS.MESSAGE_FONT_SIZE};
  color: ${Main.accent};
  width: ${Percent.full};
  padding-left: 12px;
`;

export interface Props {
  selectedVendor: Vendor;
  selectedBusinessUnit: BusinessUnit;
};

const AttributesListV2 = ({ selectedVendor, selectedBusinessUnit }: Props) => {
  const [businessUnitAttributes, setBusinessUnitAttributes] = useState<Attribute[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute>(empty);
  const [stateToReset, setStateToReset] = useState<Attribute[]>([])
  const [valuesToMerge, setValuesToMerge] = useState<AttributeValue[]>([]);
  const [mergingValues, setMergingValues] = useState<boolean>(false);
  const [changedValues, setChangedValues] = useState<ChangedValues[]>([]);
  const [addingValue, setAddingValue] = useState<boolean>(false);
  const [importModal, setImportModal] = useState<boolean>(false);
  const [attributesToMerge, setAttributesToMerge] = useState<Attribute[]>([]);
  const [mergingAttributes, setMergingAttributes] = useState(false);
  const [changedAttributes, setChangedAttributes] = useState<MergedAttributes[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);

  const { user, token } = useAuth();

  const { readOnly: addValueReadOnly } = useReadPermission(PermissionCode.ATTRIBUTES_CREATE);
  const { readOnly: editAttributeReadOnly } = useReadPermission(PermissionCode.ATTRIBUTES_EDIT);
  const { readOnly: mergeReadOnly } = useReadPermission(PermissionCode.ATTRIBUTES_MERGE);
  const { readOnly: resetReadOnly } = useReadPermission(PermissionCode.ATTRIBUTES_RESET);
  const { readOnly: importReadOnly } = useReadPermission(PermissionCode.ATTRIBUTES_IMPORT);

  const isSaveReadOnly = addValueReadOnly && editAttributeReadOnly && mergeReadOnly && resetReadOnly && importReadOnly;

  // Computed variables for better readability
  const hasSelectedVendor = !!selectedVendor;
  const hasSelectedBusinessUnit = !!selectedBusinessUnit?.id;
  const hasSelectedAttribute = selectedAttribute.name !== '';
  const shouldShowMergeControls = !isVendor(user!) && !isBrandLevelRoles(user!);

  // Determine what content to show
  const shouldShowVendorMessage = !hasSelectedVendor;
  const shouldShowBusinessUnitMessage = hasSelectedVendor && !hasSelectedBusinessUnit;
  const shouldShowAttributesList = hasSelectedVendor && hasSelectedBusinessUnit;
  const hasAttributes = businessUnitAttributes.length > 0;
  const shouldShowNoAttributesMessage = shouldShowAttributesList && !hasAttributes;
  const shouldShowAttributeMessage = shouldShowAttributesList && hasAttributes && !hasSelectedAttribute;
  const shouldShowMergeButton = shouldShowAttributesList && shouldShowMergeControls && hasAttributes;
  const shouldShowHeader = shouldShowAttributesList; // Show header whenever business unit is selected

  // Compute the actual count to display
  const displayAttributeCount = shouldShowAttributesList ? businessUnitAttributes.length : 0;

  // Helper to update attribute list
  const updateAttributeList = (attributes: Attribute[], updatedAttribute: Attribute) => {
    return attributes.map(attr =>
      attr.code === updatedAttribute.code ? updatedAttribute : attr
    );
  };

  useEffect(() => {
    if (selectedBusinessUnit?.id) {
      setSelectedAttribute(empty);
      setLoading(true);
      getAttributesPerBusinessUnit(selectedBusinessUnit.id, token!)
        .then(attributes => {
          setBusinessUnitAttributes(attributes);
          setStateToReset(attributes);
          setLoading(false);
        })
        .catch(err => {
          renderToast(toast.TYPE.ERROR, err.message || ATTRIBUTE_MESSAGES.FAILED_TO_LOAD);
          setLoading(false);
        });
    } else {
      // Clear attributes and show message when no business unit selected
      setBusinessUnitAttributes([]);
      setStateToReset([]);
      setSelectedAttribute(empty);
    }
  }, [selectedBusinessUnit]);

  const handleChange = (index: number, e: string) => {
    const newAttribute = { ...selectedAttribute, values: selectedAttribute.values.map((el, i) => i === index ? el = { ...el, name: e } : el) };

    setSelectedAttribute(newAttribute);
    setBusinessUnitAttributes(updateAttributeList(businessUnitAttributes, newAttribute));
  };

  const displayValues = (code: string) => {
    const foundAttribute = businessUnitAttributes.find(el => el.code === code);

    if (selectedAttribute.code === code) {
      setSelectedAttribute(empty);
    } else {
      setSelectedAttribute(foundAttribute!);
    }
  };

  const onDragEndAttributes = (result: any) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const reorderedAttributes = move(businessUnitAttributes, source.index, destination.index)

    setBusinessUnitAttributes(reorderedAttributes);
  };

  const onDragEndValues = (result: any) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const reorderedValues = move(selectedAttribute.values, source.index, destination.index);
    const newAttribute = { ...selectedAttribute, values: reorderedValues }

    setSelectedAttribute(newAttribute);
    setBusinessUnitAttributes(updateAttributeList(businessUnitAttributes, newAttribute));
  };

  const addValue = (newValue: AttributeValueRequest) => {
    setAddingValue(false);

    const newAttribute = { ...selectedAttribute, values: [...selectedAttribute.values, { ...newValue, order: newValue.displayOrder }] }
    setSelectedAttribute(newAttribute);
    setBusinessUnitAttributes(updateAttributeList(businessUnitAttributes, newAttribute));
  };

  const importXLSXAttributeValue = (newAttributeValues: AttributeValueRequest[]) => {
    const newAttribute = {
      ...selectedAttribute,
      values: [
        ...selectedAttribute.values,
        ...newAttributeValues.map((newValue) => ({
          id: newValue.id,
          attributeId: newValue.attributeId,
          code: newValue.code,
          name: newValue.name,
          displayOrder: newValue.displayOrder,
          order: newValue.displayOrder
        }))
      ]
    };
    setSelectedAttribute(newAttribute);
    setBusinessUnitAttributes(updateAttributeList(businessUnitAttributes, newAttribute));
  }

  const setChecked = (index: number) => {
    if (!valuesToMerge.some(el => el.code === selectedAttribute.values[index].code)) {
      setValuesToMerge([...valuesToMerge, selectedAttribute.values[index]]);
    } else {
      setValuesToMerge(valuesToMerge.filter(el => el.code !== selectedAttribute.values[index].code))
    }
  };

  const setCheckedAttribute = (index: number) => {
    if (!attributesToMerge.some(el => el.code === businessUnitAttributes[index].code)) {
      setAttributesToMerge([...attributesToMerge, businessUnitAttributes[index]]);
    } else {
      setAttributesToMerge(attributesToMerge.filter(el => el.code !== businessUnitAttributes[index].code));
    }
  };

  const onModalClose = () => {
    if (mergingValues || valuesToMerge.length > 0) {
      setMergingValues(false);
      setValuesToMerge([]);
    }

    if (mergingAttributes || attributesToMerge.length > 0) {
      setMergingAttributes(false);
      setAttributesToMerge([]);
    }
    setImportModal(false);
    setAddingValue(false);
  };

  const toggleMerging = () => {
    setMergingValues(!mergingValues);
  };

  const sort = (direction: 'asc' | 'desc') => {
    setSelectedAttribute({ ...selectedAttribute, values: orderByArray(selectedAttribute.values, 'name', direction) });
  };

  const toggleMergingAttributes = () => {
    setMergingAttributes(!mergingAttributes);
  };

  const resetWithoutSave = () => {
    const resetSelectedAttribute = stateToReset.find(attr => attr.code === selectedAttribute.code)!;

    setSelectedAttribute(resetSelectedAttribute);
    setBusinessUnitAttributes(stateToReset);
  };

  const mergeModalValues = (newValue: AttributeValue) => {
    const filteredValues = selectedAttribute.values.filter(el => !valuesToMerge.map(v => v.code).includes(el.code));

    const newAttribute = {
      ...selectedAttribute,
      values: [...filteredValues.map((el, i) => { return { ...el, order: i } }), { ...newValue, order: filteredValues.length + 1 }]
    };

    const changes = {
      attributeCode: selectedAttribute.code,
      oldValues: valuesToMerge,
      newValue: newValue
    }
    setChangedValues([...changedValues, changes]);

    setSelectedAttribute(newAttribute);
    setBusinessUnitAttributes(updateAttributeList(businessUnitAttributes, newAttribute));

    setValuesToMerge([]);
    setMergingValues(false);
  };

  const mergeModalAttributes = (selectedCode: string, selectedName: string) => {
    const mergedAttribute = mergeAttributes([...attributesToMerge], selectedCode, selectedName);
    const filteredAttributes = businessUnitAttributes.filter(el => !attributesToMerge.map(a => a.code).includes(el.code));

    setBusinessUnitAttributes([...filteredAttributes, mergedAttribute]);
    setSelectedAttribute(mergedAttribute);
    setChangedAttributes([...changedAttributes, { oldAttributes: [...attributesToMerge], newAttribute: mergedAttribute }]);
    setAttributesToMerge([]);
    setMergingAttributes(false);

    renderToast(toast.TYPE.SUCCESS, ATTRIBUTE_MESSAGES.ATTRIBUTES_MERGED_SUCCESS);
  };

  const updateBusinessUnitAttributes = () => {
    const reorderRequestArray = businessUnitAttributes.map(el => ({ id: el.id!, displayOrder: el.order, values: el.values.map(v => ({ id: v.id!, displayOrder: v.order })) }))

    renderToast(toast.TYPE.INFO, ATTRIBUTE_MESSAGES.SAVING_ATTRIBUTES, {
      toastId: ATTRIBUTE_TOAST_IDS.UPDATE_ATTRIBUTES,
      autoClose: false
    });

    reorderRequest(token as string,{ attributes: reorderRequestArray })
      .then(result => {
        updateToast(ATTRIBUTE_TOAST_IDS.UPDATE_ATTRIBUTES, toast.TYPE.SUCCESS, ATTRIBUTE_MESSAGES.ATTRIBUTES_UPDATED_SUCCESS, {
          autoClose: 5000
        });
      }).catch(err => {
        updateToast(ATTRIBUTE_TOAST_IDS.UPDATE_ATTRIBUTES, toast.TYPE.ERROR, err.message, {
          autoClose: 5000
        });
      });

    setChangedValues([]);
    setChangedAttributes([]);
  };

  const attributeNames = businessUnitAttributes.map((el, i) => {
    return (
      <Draggable draggableId={el.code} key={el.code} index={i}>
        {(provided) => (
          <ListItem
            ref={provided.innerRef}
            {...provided.dragHandleProps}
            {...provided.draggableProps}
            active={selectedAttribute.code === el.code}
            key={el.code}
            onClick={() => displayValues(el.code)}
          >
            <FlexboxContainer maxWidth="100%" justifyContent="space-between" className='heading'>
              {mergingAttributes && <Checkbox type="checkbox" onChange={() => setCheckedAttribute(i)} />}
              {i + 1}. {el.name}
              <ValuesCount>({el.values.length})</ValuesCount>
            </FlexboxContainer>
          </ListItem>
        )}
      </Draggable>
    );
  });

  const renderAttributesListContent = () => {
    if (shouldShowVendorMessage) {
      return (
        <EmptyMessage className='heading'>
          {ATTRIBUTE_MESSAGES.PLEASE_SELECT_VENDOR}
        </EmptyMessage>
      );
    }

    if (shouldShowBusinessUnitMessage) {
      return (
        <EmptyMessage>
          {ATTRIBUTE_MESSAGES.PLEASE_SELECT_BUSINESS_UNIT}
        </EmptyMessage>
      );
    }

    
    if (shouldShowNoAttributesMessage) {
      return (
        <EmptyMessage className='heading'>
          {ATTRIBUTE_MESSAGES.NO_ATTRIBUTES_AVAILABLE}
        </EmptyMessage>
      );
    }

    return (
      <Droppable droppableId="attributes-list">
        {(provided) => (
          <List
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {attributeNames}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    );
  };

  if (loading) {
      return (
        <FlexboxContainer justifyContent="center" alignItems="center" minWidth="100%">
          <LoadingIndicator />
        </FlexboxContainer>
      );
  }

  return (
    <FlexboxContainer justifyContent="flex-start" minWidth="100%">
      <MergeAttributeModal
        valuesToMerge={valuesToMerge}
        mergeValues={mergeModalValues}
        onClose={onModalClose}
        showModal={!mergingValues && valuesToMerge.length > 0}
      />
      <MergeAttributeModal
        attributesToMerge={attributesToMerge}
        mergeAttributes={mergeModalAttributes}
        onClose={onModalClose}
        showModal={!mergingAttributes && attributesToMerge.length > 0}
      />
      <AddAttributeValueModal
        attribute={selectedAttribute}
        addValue={addValue}
        onClose={onModalClose}
        title={ATTRIBUTE_CONSTANTS.ADD_VALUE_MODAL_TITLE}
        showModal={addingValue}
      />
      <ImportXLSXAttributeValueModal
        attribute={selectedAttribute}
        importXLSX={importXLSXAttributeValue}
        onClose={onModalClose}
        title={ATTRIBUTE_CONSTANTS.IMPORT_TITLE}
        showModal={importModal}
      />
      {hasSelectedBusinessUnit && selectedBusinessUnit?.id && (
        <AddBulkAttributesModal
          showModal={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          businessUnitId={selectedBusinessUnit.id}
          existingAttributeNames={businessUnitAttributes.map(attr => attr.name)}
          onSuccess={() => {
            getAttributesPerBusinessUnit(selectedBusinessUnit.id!, token!)
              .then(attributes => {
                setBusinessUnitAttributes(attributes);
                setStateToReset(attributes);
              })
              .catch(err => {
                renderToast(toast.TYPE.ERROR, err.message || ATTRIBUTE_MESSAGES.FAILED_TO_LOAD);
              });
          }}
        />
      )}
      <FlexboxContainer minWidth="100%" flexDirection="column">
        {shouldShowHeader && (
          <FlexboxContainer minWidth="100%" padding="12px" justifyContent="space-between" bgColor={Background.white} boxShadow="0px 5px lightgrey" margin="0 0 8px 0">
            <AttributesTitle>
              <PageHeading>Attributes - ({displayAttributeCount})</PageHeading>
              <StyledInfoIcon tooltipText={ATTRIBUTE_TOOLTIPS.ATTRIBUTES_LIST} />
            </AttributesTitle>
            <FlexboxContainer gap="12px">
              <ReadOnlyWrapper readOnly={addValueReadOnly}>
                <Button onClick={() => setShowBulkModal(true)}>{ATTRIBUTE_BUTTONS.ADD_ATTRIBUTE}</Button>
              </ReadOnlyWrapper>
              {hasAttributes && (
                <ReadOnlyWrapper readOnly={isSaveReadOnly}>
                  <Button disabled={selectedAttribute.values.map(el => el.name).some(el => el === '')} onClick={updateBusinessUnitAttributes}>{ATTRIBUTE_BUTTONS.SAVE}</Button>
                </ReadOnlyWrapper>
              )}
            </FlexboxContainer>
          </FlexboxContainer>
        )}
        <FlexboxContainer>
          <FlexboxContainer flexDirection="column" minWidth="25%">
            {shouldShowMergeButton && (
              <FlexboxContainer minWidth="100%" padding="12px" justifyContent="center" bgColor={Background.white} boxShadow="0px 5px lightgrey" margin="0 0 8px 0">
                <ReadOnlyWrapper readOnly={mergeReadOnly}>
                  <Button onClick={toggleMergingAttributes}>
                    {mergingAttributes ? ATTRIBUTE_BUTTONS.MERGE : ATTRIBUTE_BUTTONS.SELECT_TO_MERGE}
                  </Button>
                </ReadOnlyWrapper>
              </FlexboxContainer>
            )}
            <DragDropContext onDragEnd={onDragEndAttributes}>
              {renderAttributesListContent()}
            </DragDropContext>
          </FlexboxContainer>
          {shouldShowAttributesList && hasSelectedAttribute && (
            <DragDropContext onDragEnd={onDragEndValues}>
              <DroppableColumn
                droppableId="attribute-values-list"
                selectedAttribute={selectedAttribute}
                changeValue={handleChange}
                setAdding={setAddingValue}
                setImportModal={setImportModal}
                updateVendorAttributes={updateBusinessUnitAttributes}
                toggleMerging={toggleMerging}
                mergingValues={mergingValues}
                setChecked={setChecked}
                sort={sort}
                resetWithoutSave={resetWithoutSave}
                addValueReadOnly={addValueReadOnly}
                resetReadOnly={resetReadOnly}
                mergeReadOnly={mergeReadOnly}
                importReadOnly={importReadOnly}
                editAttributeReadOnly={editAttributeReadOnly}
              />
            </DragDropContext>
          )}
          {shouldShowAttributeMessage && (
            <FlexboxContainer flexDirection="column" minWidth="75%">
              <SelectAttributeMessage className='heading'>
                {ATTRIBUTE_MESSAGES.PLEASE_SELECT_ATTRIBUTE}
              </SelectAttributeMessage>
            </FlexboxContainer>
          )}
        </FlexboxContainer>
      </FlexboxContainer>
    </FlexboxContainer >
  );
};

export default AttributesListV2;
